import React, { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { Check, Sparkles, ShieldCheck, CreditCard } from "lucide-react";
import TapCardModal from "@/components/billing/TapCardModal";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { UsagePanel } from "@/components/UsagePanel";

export default function PlansPage() {
  const { lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [cycle, setCycle] = useState("monthly");

  const [tapModalOpen, setTapModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [p, b] = await Promise.all([
        api.get("/plans"),
        api.get("/billing").catch(() => ({ data: {} })),
      ]);
      setPlans(p.data.plans || []);
      setBilling(b.data);
      const c = b.data?.subscription?.billing_cycle;
      if (c === "monthly" || c === "yearly") setCycle(c);
    } catch (e) {
      setLoadError(e?.message || "load_failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Auto-open the Tap Payments modal when arrived from onboarding or the
  // Billing page. Those flows pass `?plan_id=X&billing_cycle=Y&from=...`
  // in the query string — we wait for `plans` to load, match by plan_id,
  // then open the same TapCardModal that any manual "Select plan" click
  // would open. This is the seamless replacement for the retired legacy
  // /billing/change-plan mocked path.
  useEffect(() => {
    if (!plans.length) return;
    const params = new URLSearchParams(location.search);
    const planId = params.get("plan_id");
    const bc = params.get("billing_cycle");
    if (!planId || (bc !== "monthly" && bc !== "yearly")) return;
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    setCycle(bc);
    setSelectedPlan(plan);
    setTapModalOpen(true);
    // Strip the query so a refresh doesn't re-open the modal.
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  // Handle Tap redirect return (?tap_status=success|declined|error&tap_message=…)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("tap_status");
    if (!status) return;
    const tapMessage = params.get("tap_message");
    if (status === "success") {
      toast.success(lang === "ar" ? "تم تفعيل تجربتك المجانية بنجاح" : "Trial activated successfully");
      load();
    } else if (status === "declined") {
      const base = lang === "ar" ? "تم رفض البطاقة" : "Card was declined";
      // Surface Tap's actual error (e.g. "Payment Agreement timed out", "Insufficient funds")
      toast.error(tapMessage ? `${base}: ${tapMessage}` : `${base}. ${lang === "ar" ? "حاول ببطاقة أخرى." : "Try another card."}`);
    } else {
      const base = lang === "ar" ? "لم يكتمل التحقق من البطاقة" : "Card verification did not complete";
      toast.error(tapMessage ? `${base}: ${tapMessage}` : base);
    }
    // Clean URL
    navigate(location.pathname, { replace: true });
  }, [location.search]);

  const openPayment = (plan) => {
    setSelectedPlan(plan);
    setTapModalOpen(true);
  };
  const closePayment = () => {
    setTapModalOpen(false);
    setSelectedPlan(null);
    load();
  };

  const current = billing?.subscription;
  const isCurrentPlan = (planId) =>
    current?.plan_id === planId && current?.billing_cycle === cycle && ["trialing", "active"].includes(current?.status);
  const isSubscribedTo = (planId) =>
    current?.plan_id === planId && ["trialing", "active"].includes(current?.status);

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl">{lang === "ar" ? "خطط الاشتراك" : "Subscription Plans"}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {lang === "ar"
            ? "اختر الخطة المناسبة لنمو أعمالك. جميع الخطط تشمل تجربة مجانية ٧ أيام مع التحقق من البطاقة عبر Tap Payments."
            : "Choose the plan that fits your growth. All plans include a 7-day free trial with card verification via Tap Payments."}
        </p>
      </div>

      {/* Current-cycle usage snapshot (Critical Task 4) */}
      <UsagePanel />

      {/* Current subscription banner */}
      {current && ["trialing", "active", "past_due"].includes(current.status) && (
        <div data-testid="current-sub-banner" className="mb-8 nx-card p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-medium">
                {lang === "ar" ? (current.plan_name_ar || current.plan_name) : current.plan_name}
                {" · "}
                <span className="tabular-nums">{current.amount} {current.currency || "SAR"}</span>
                {" / "}
                {current.billing_cycle === "monthly" ? (lang === "ar" ? "شهر" : "mo") : (lang === "ar" ? "سنة" : "yr")}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {current.status === "trialing" && current.trial_ends_at && (
                  <>{lang === "ar" ? "تجربة مجانية حتى" : "Free trial until"} <span className="tabular-nums">{new Date(current.trial_ends_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}</span></>
                )}
                {current.status === "active" && current.next_billing_at && (
                  <>{lang === "ar" ? "التجديد القادم" : "Next renewal"} <span className="tabular-nums">{new Date(current.next_billing_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}</span></>
                )}
                {current.status === "past_due" && (lang === "ar" ? "فشل الخصم — الرجاء تحديث بطاقتك" : "Payment failed — please update your card")}
              </div>
            </div>
          </div>
          {billing?.payment_method?.kind === "card" && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="current-payment-method">
              <CreditCard className="w-4 h-4" />
              <span className="uppercase tracking-widest">{billing.payment_method.brand}</span>
              <span className="tabular-nums">•••• {billing.payment_method.last4}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-background border border-border rounded-sm p-1" data-testid="plans-cycle-toggle">
          <button data-testid="plans-cycle-monthly" onClick={() => setCycle("monthly")}
            className={`px-6 py-2 text-sm rounded-sm transition ${cycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            {lang === "ar" ? "شهري" : "Monthly"}
          </button>
          <button data-testid="plans-cycle-yearly" onClick={() => setCycle("yearly")}
            className={`px-6 py-2 text-sm rounded-sm transition inline-flex items-center gap-2 ${cycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <span>{lang === "ar" ? "سنوي" : "Yearly"}</span>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${cycle === "yearly" ? "bg-white/20" : "bg-emerald-500/10 text-emerald-600 border border-emerald-500/30"}`}>
              {lang === "ar" ? "شهران مجانًا" : "2 months free"}
            </span>
          </button>
        </div>
      </div>

      {loading && (
        <div data-testid="plans-loading" className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="nx-card p-8 h-96 animate-pulse">
              <div className="h-6 w-32 bg-muted/50 rounded-sm mb-4" />
              <div className="h-10 w-24 bg-muted/50 rounded-sm mb-6" />
              <div className="space-y-2">
                {[0, 1, 2, 3, 4].map((k) => <div key={k} className="h-3 bg-muted/40 rounded-sm" />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && loadError && (
        <div data-testid="plans-error" className="nx-card p-8 border border-destructive/30 bg-destructive/5 max-w-lg mx-auto text-center">
          <div className="text-sm mb-4">{lang === "ar" ? "تعذّر تحميل الخطط. حاول مرة أخرى." : "Failed to load plans. Try again."}</div>
          <button data-testid="plans-retry" onClick={load} className="nx-btn-accent">
            {lang === "ar" ? "إعادة المحاولة" : "Retry"}
          </button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-grid">
            {plans.map((p) => {
              const isCurrent = isCurrentPlan(p.id);
              const isSubscribedOtherCycle = isSubscribedTo(p.id) && !isCurrent;
              const price = p.prices?.[cycle];
              const name = lang === "ar" ? (p.name_ar || p.name) : p.name;
              const cta = isCurrent
                ? (lang === "ar" ? "خطتك الحالية" : "Current plan")
                : isSubscribedOtherCycle
                  ? (lang === "ar" ? "التبديل إلى هذه الدورة" : "Switch to this cycle")
                  : (lang === "ar" ? "ابدأ التجربة المجانية" : "Start free trial");
              return (
                <div key={p.id} data-testid={`plans-card-${p.id}`}
                  className={`nx-card p-8 flex flex-col relative hover:-translate-y-1 transition-transform ${
                    p.popular ? "border-[#B8875D] ring-1 ring-[#B8875D]/40 shadow-lg" : ""
                  }`}>
                  {p.popular && (
                    <div className="absolute -top-3 start-8 bg-[#B8875D] text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {lang === "ar" ? "الأكثر شعبية" : "Most popular"}
                    </div>
                  )}
                  <h3 className="font-display text-2xl">{name}</h3>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-display text-5xl tabular-nums">{price}</span>
                    <span className="text-sm text-muted-foreground">
                      {p.currency || "SAR"} / {cycle === "monthly" ? (lang === "ar" ? "شهر" : "mo") : (lang === "ar" ? "سنة" : "yr")}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground" data-testid={`plans-vat-inclusive-${p.id}`}>
                    {lang === "ar" ? "شامل ضريبة القيمة المضافة ١٥٪" : "VAT (15%) included"}
                  </div>
                  <div className="mt-2 text-xs text-emerald-600">
                    {lang === "ar" ? "٧ أيام تجربة مجانية — تحقق آمن من البطاقة" : "7-day free trial — secure card verification"}
                  </div>
                  <ul className="mt-6 space-y-3 text-sm flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex gap-2">
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`plans-select-${p.id}`}
                    onClick={() => openPayment(p)}
                    disabled={isCurrent}
                    className={`mt-8 ${
                      isCurrent
                        ? "bg-muted text-muted-foreground py-3 rounded-sm text-sm cursor-not-allowed"
                        : p.popular
                          ? "nx-btn-accent"
                          : "nx-btn-primary"
                    }`}
                  >
                    {cta}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-10 nx-card p-5 max-w-3xl mx-auto text-center" data-testid="plans-guarantee">
            <div className="font-display text-lg mb-2">
              {lang === "ar" ? "١٤ يومًا لضمان الرضا · إلغاء في أي وقت" : "14-day satisfaction guarantee · Cancel anytime"}
            </div>
            <p className="text-sm text-muted-foreground">
              {lang === "ar"
                ? "الدفع الآمن عبر Tap Payments بمعايير PCI-DSS. ندعم مدى وفيزا وماستركارد وأمريكان إكسبريس. لن يتم أي خصم حتى انتهاء تجربتك المجانية بعد ٧ أيام."
                : "Secure payments via Tap Payments (PCI-DSS certified). We support MADA, Visa, Mastercard, and Amex. No charge occurs until your 7-day free trial ends."}
            </p>
            <div data-testid="plans-legal-links" className="mt-4 text-xs text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link to="/terms" className="underline hover:text-foreground">{lang === "ar" ? "شروط الخدمة" : "Terms"}</Link>
              <Link to="/refund" className="underline hover:text-foreground">{lang === "ar" ? "سياسة الاسترداد" : "Refund policy"}</Link>
              <Link to="/cancellation" className="underline hover:text-foreground">{lang === "ar" ? "الإلغاء" : "Cancellation"}</Link>
              <Link to="/support" className="underline hover:text-foreground">{lang === "ar" ? "الدعم" : "Contact support"}</Link>
            </div>
          </div>
        </>
      )}

      <TapCardModal
        open={tapModalOpen}
        plan={selectedPlan}
        cycle={cycle}
        onClose={closePayment}
        onSuccess={() => load()}
      />
    </AppShell>
  );
}
